/**
 * Lightweight fuzzy matching for the search bar. No dependencies.
 *
 * `fuzzyScore` returns 0 for no match, or a positive score where higher is
 * better. Subsequence matches with tight, early, word-boundary-aligned hits
 * score highest — the same intuition as editor "fuzzy file open".
 */
export function fuzzyScore(query: string, target: string): number {
  if (!query) return 1;
  const q = query.toLowerCase();
  const t = target.toLowerCase();

  // Exact / prefix / substring fast-paths dominate subsequence scoring.
  if (t === q) return 1000;
  if (t.startsWith(q)) return 800 - (t.length - q.length);
  const idx = t.indexOf(q);
  if (idx !== -1) return 600 - idx - (t.length - q.length) * 0.1;

  // Subsequence scoring.
  let score = 0;
  let ti = 0;
  let consecutive = 0;
  for (let qi = 0; qi < q.length; qi++) {
    const ch = q[qi];
    let found = -1;
    for (; ti < t.length; ti++) {
      if (t[ti] === ch) {
        found = ti;
        break;
      }
    }
    if (found === -1) return 0; // a query char wasn't found in order
    // Bonus for consecutive matches and word-boundary alignment.
    const atBoundary = found === 0 || /[\s|_\-/]/.test(t[found - 1] ?? "");
    consecutive = found === ti - 0 ? consecutive + 1 : 1;
    score += 10 + consecutive * 2 + (atBoundary ? 8 : 0);
    ti = found + 1;
  }
  // Penalise length mismatch slightly so shorter targets win ties.
  return Math.max(1, score - (t.length - q.length) * 0.05);
}

export interface RankedResult<T> {
  item: T;
  score: number;
}

/** Rank a list against a query, dropping non-matches. */
export function fuzzyRank<T>(
  query: string,
  items: T[],
  keyFn: (item: T) => string,
): RankedResult<T>[] {
  if (!query.trim()) return items.map((item) => ({ item, score: 1 }));
  return items
    .map((item) => ({ item, score: fuzzyScore(query, keyFn(item)) }))
    .filter((r) => r.score > 0)
    .sort((a, b) => b.score - a.score);
}
